/** Type-ahead matching for the customer-search panel (Figma 2464:2501, right pane).
 *
 * Pure and separate from the component so the matching rules are testable and Fast Refresh keeps
 * component state (`react-doctor/only-export-components`).
 *
 * ⚠ These rules are DEMO behaviour. The real search is a backend endpoint (TechSpec B6) with GDPR
 * guardrails the PRD §6 spells out: type-ahead prefill ONLY for customers with a prior
 * relationship at this business, a complete identifier required for anyone else (no partial-match
 * browsing, which would let a merchant enumerate the base), alias + masked identifier in results,
 * every search audit-logged and rate-limited. Matching locally over a full list, as below, is
 * exactly what must NOT ship — it is here so the flow can be tested before B6 exists.
 */
import type { SearchCustomer } from '../api/types'

export type SearchField = 'phone' | 'name' | 'email'

/** Strip everything except digits, so "079 412 88 03", "+41 79 412 88 03" and "0794128803" all
 * compare equal. Cashiers read numbers off a screen in whatever grouping the customer used. */
export function digitsOnly(value: string): string {
  return value.replace(/\D/g, '')
}

/** Accent- and case-insensitive fold — the cashier types "meyer" for "Meyer" and "poke" for "Poké". */
function fold(value: string): string {
  return value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
}

/** Filter the customer list for the active search field.
 *
 * An empty query returns everyone — the panel shows them under "Récents", which is the fast path
 * the design is built around (tap a familiar name, no typing at all).
 */
export function matchCustomers(
  customers: SearchCustomer[],
  query: string,
  field: SearchField,
): SearchCustomer[] {
  const raw = query.trim()
  if (!raw) return customers

  if (field === 'phone') {
    const digits = digitsOnly(raw)
    // A query of punctuation only ("+", " ") carries no signal — treat it as no filter rather than
    // matching every customer on the empty string.
    if (!digits) return customers
    return customers.filter((c) => {
      // Substring match on text, NOT a set-membership test — a Set cannot answer "contains", so
      // the scan here is inherent to the search rather than a lookup that wants indexing.
      const text = digitsOnly(c.phone ?? '')
      return text.includes(digits)
    })
  }

  const q = fold(raw)
  return customers.filter((c) => {
    const text = fold(field === 'email' ? (c.email ?? '') : c.display_name)
    return text.includes(q)
  })
}

/** Initial shown in the avatar bubble. Falls back to "?" rather than rendering an empty circle. */
export function initialFor(customer: SearchCustomer): string {
  return customer.display_name.trim().charAt(0).toUpperCase() || '?'
}
